import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { PO_STATUS_META } from "@/lib/inventory";
import { formatDate } from "@/lib/format";
import {
  Package, MapPin, Sliders, Loader2, ChevronDown, ChevronRight, Plus, Trash2, Save, CheckCircle2, X, ScanLine, Upload, Shuffle, Users, Clock, AlertTriangle, BarChart3, Download, FileSpreadsheet, TrendingUp, BookOpen,
} from "lucide-react";
import { formatDuration, CONDITION_META } from "@/lib/inventory";
import POWizard from "@/components/POWizard";
import LocationAssignGrid from "@/components/LocationAssignGrid";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const SUBTABS = [
  { v: "pos", l: "Purchase Orders", icon: Package },
  { v: "analytics", l: "Analytics", icon: BarChart3 },
  { v: "locations", l: "Locations", icon: MapPin },
  { v: "import", l: "Import Stock", icon: Upload },
  { v: "export", l: "ShipStation Export", icon: FileSpreadsheet },
  { v: "defaults", l: "Product Defaults", icon: Sliders },
];
const inputCls = "px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40";
const StatusPill = ({ s }) => { const m = PO_STATUS_META[s] || { label: s, cls: "bg-slate-100 text-slate-600" }; return <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${m.cls}`}>{m.label}</span>; };

// ---- PO detail + planning (expanded row) ----
function PODetail({ poId, onStatus }) {
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [adding, setAdding] = useState({});
  const [rcv, setRcv] = useState(null);
  const [slog, setSlog] = useState(null);
  const [showLog, setShowLog] = useState(false);

  const load = () => api.get(`/inventory/pos/${poId}`).then(({ data }) => {
    setPo(data);
    if (["receiving", "discrepancy_review", "completed"].includes(data.status)) {
      api.get(`/inventory/pos/${poId}/receiving-summary`).then(({ data: s }) => setRcv(s)).catch(() => {});
      api.get(`/inventory/pos/${poId}/scan-log`).then(({ data: s }) => setSlog(s)).catch(() => {});
    }
  }).catch(() => {});
  useEffect(() => { load(); }, [poId]);

  if (!po) return <div className="text-slate-400 text-sm flex items-center gap-2 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  const plannedFor = (lineId) => po.planned.filter((p) => p.po_line_id === lineId);

  const addPlanned = async (lineId) => {
    const a = adding[lineId] || {};
    if (!a.code || !a.qty) { toast.error("Enter a location code and quantity"); return; }
    try {
      await api.post(`/inventory/pos/${poId}/planned`, { po_line_id: lineId, location_code: a.code, planned_qty: Number(a.qty) });
      setAdding((s) => ({ ...s, [lineId]: {} }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delPlanned = async (id) => { try { await api.delete(`/inventory/planned/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const setStatus = async (status) => {
    try { await api.patch(`/inventory/pos/${poId}`, { status }); toast.success(`Marked ${PO_STATUS_META[status]?.label || status}`); load(); onStatus?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const NEXT = { submitted: "approved", needs_review: "approved", approved: "location_planning", location_planning: "ready_to_receive" };
  const next = NEXT[po.status];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500">Status:</span> <StatusPill s={po.status} />
        {next && <button onClick={() => setStatus(next)} data-testid="po-advance-btn" className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md"><CheckCircle2 className="w-3.5 h-3.5" /> Move to {PO_STATUS_META[next]?.label}</button>}
        {["approved", "location_planning", "ready_to_receive", "receiving", "discrepancy_review", "completed", "partially_received"].includes(po.status) && (
          <button onClick={() => navigate(`/receiving-inventory?po=${poId}`)} data-testid="po-start-receiving" className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-3 py-1.5 rounded-md"><ScanLine className="w-3.5 h-3.5" /> {po.status === "receiving" || po.status === "completed" ? "Receive more" : "Start receiving"}</button>
        )}
        {po.status !== "cancelled" && po.status !== "completed" && <button onClick={() => setStatus("cancelled")} data-testid="po-cancel-btn" className="text-xs font-semibold text-red-500 hover:text-red-700 px-2">Cancel</button>}
        {["receiving", "discrepancy_review", "completed"].includes(po.status) && (
          <button onClick={() => window.open(`${API_BASE}/inventory/pos/${poId}/reconciliation?format=xlsx`, "_blank", "noopener")} data-testid="po-recon-sheet" className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-md"><FileSpreadsheet className="w-3.5 h-3.5" /> Review sheet (QC)</button>
        )}
        {["receiving", "discrepancy_review", "completed"].includes(po.status) && (
          <button onClick={() => window.open(`${API_BASE}/inventory/export/shipstation?account_id=${po.account_id}&format=xlsx`, "_blank", "noopener")} data-testid="po-shipstation-sheet" className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 rounded-md"><FileSpreadsheet className="w-3.5 h-3.5" /> ShipStation sheet</button>
        )}
      </div>

      {slog && slog.expected_units > 0 && (
        <div data-testid="po-receive-bar">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-1">
            <span>Received {slog.received_units} of {slog.expected_units} units</span>
            <span>{Math.min(100, Math.round((slog.received_units / slog.expected_units) * 100))}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.min(100, Math.round((slog.received_units / slog.expected_units) * 100))}%` }} />
          </div>
        </div>
      )}

      {slog && slog.count > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white" data-testid="po-scan-log">
          <button onClick={() => setShowLog((v) => !v)} data-testid="po-scan-log-toggle" className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700">
            <span className="inline-flex items-center gap-2"><ScanLine className="w-4 h-4 text-slate-400" /> Scan log — {slog.count} scans</span>
            {showLog ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {showLog && (
            <div className="max-h-80 overflow-y-auto border-t border-slate-100">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-slate-50"><tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 px-3 font-medium">When</th><th className="py-2 px-3 font-medium">User</th><th className="py-2 px-3 font-medium">SKU</th><th className="py-2 px-3 font-medium">Qty</th><th className="py-2 px-3 font-medium">Bin</th><th className="py-2 px-3 font-medium">Type</th>
                </tr></thead>
                <tbody>
                  {slog.events.map((e, i) => (
                    <tr key={`${e.at}-${i}`} className={`border-t border-slate-50 ${e.reversed ? "opacity-40 line-through" : ""} ${e.event_type?.includes("reverse") ? "bg-red-50/40" : ""}`} data-testid={`po-scan-${i}`}>
                      <td className="py-1.5 px-3 whitespace-nowrap text-slate-600">{e.at ? new Date(e.at).toLocaleString() : "—"}</td>
                      <td className="py-1.5 px-3 text-slate-700">{e.user?.split("@")[0] || "—"}</td>
                      <td className="py-1.5 px-3 font-mono">{e.sku || e.barcode || "—"}</td>
                      <td className="py-1.5 px-3 tabular-nums font-semibold">{e.quantity}</td>
                      <td className="py-1.5 px-3 font-mono">{e.location_code || "—"}</td>
                      <td className="py-1.5 px-3 text-slate-500">{e.event_type?.replace(/_/g, " ")}{e.condition && e.condition !== "good" ? ` · ${e.condition}` : ""}{e.verify_skipped ? <span className="ml-1.5 text-[10px] font-bold uppercase text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`po-scan-unverified-${i}`}>bin not verified</span> : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {rcv && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="po-receiving-summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-emerald-600">Good (sellable)</p><p className="text-xl font-extrabold text-emerald-700" data-testid="po-rcv-good">{rcv.tallies?.good || 0}</p></div>
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-red-600">Held (damaged/quarantine)</p><p className="text-xl font-extrabold text-red-700" data-testid="po-rcv-hold">{rcv.tallies?.hold || 0}</p></div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Hands-on time</p><p className="text-xl font-extrabold text-slate-800" data-testid="po-rcv-duration">{formatDuration(rcv.duration_seconds)}</p></div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2"><p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> Staff</p><p className="text-xl font-extrabold text-slate-800">{(rcv.contributors || []).length}</p></div>
          </div>
          {(rcv.contributors || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="po-rcv-contributors">
              {rcv.contributors.map((c) => <span key={c.user} className="text-xs font-semibold bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full">{c.user?.split("@")[0]} · {c.units} ({c.good}g/{c.hold}h)</span>)}
            </div>
          )}
          {(rcv.exceptions || []).length > 0 && (
            <div data-testid="po-rcv-exceptions">
              <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold mb-1.5 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Unexpected items ({rcv.exceptions.length})</p>
              <div className="space-y-1">
                {rcv.exceptions.map((e) => (
                  <div key={e.id} className="flex items-center justify-between gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                    <span className="font-mono truncate">+{e.quantity} {e.sku || e.barcode || "?"} → {e.location_code || "—"}{e.condition !== "good" ? ` [${CONDITION_META[e.condition]?.l}]` : ""}</span>
                    <span className="text-slate-500 shrink-0">{(e.user || "").split("@")[0]}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {["submitted", "needs_review", "approved", "location_planning", "ready_to_receive", "receiving", "discrepancy_review"].includes(po.status) && po.lines?.length > 0 && (
        <LocationAssignGrid poId={poId} lines={po.lines} planned={po.planned || []} onSaved={load} />
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200"><th className="py-2 px-3 font-medium">Product</th><th className="py-2 px-3 font-medium">SKU</th><th className="py-2 px-3 font-medium">Barcode</th><th className="py-2 px-3 font-medium">Exp.</th><th className="py-2 px-3 font-medium">Planned locations</th></tr></thead>
          <tbody>
            {po.lines.map((l) => (
              <tr key={l.id} className="border-b border-slate-50 align-top" data-testid={`po-line-${l.id}`}>
                <td className="py-2 px-3 text-slate-700">{l.product_title || "—"}</td>
                <td className="py-2 px-3 font-mono text-xs">{l.sku}</td>
                <td className="py-2 px-3 font-mono text-xs">{l.barcode}</td>
                <td className="py-2 px-3 text-slate-700">{l.expected_qty}</td>
                <td className="py-2 px-3">
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {plannedFor(l.id).map((p) => (
                      <span key={p.id} className="inline-flex items-center gap-1 text-xs font-semibold bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                        {p.location_code} ({p.planned_qty})
                        <button onClick={() => delPlanned(p.id)} data-testid={`plan-delete-${p.id}`} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <input placeholder="G-C4" value={adding[l.id]?.code || ""} onChange={(e) => setAdding((s) => ({ ...s, [l.id]: { ...s[l.id], code: e.target.value } }))} data-testid={`plan-code-${l.id}`} className="w-20 px-2 py-1 border border-slate-200 rounded text-xs uppercase" />
                    <input placeholder="qty" type="number" value={adding[l.id]?.qty || ""} onChange={(e) => setAdding((s) => ({ ...s, [l.id]: { ...s[l.id], qty: e.target.value } }))} data-testid={`plan-qty-${l.id}`} className="w-14 px-2 py-1 border border-slate-200 rounded text-xs" />
                    <button onClick={() => addPlanned(l.id)} data-testid={`plan-add-${l.id}`} className="text-blue-600 hover:text-blue-800"><Plus className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function POsTab() {
  const [accounts, setAccounts] = useState([]);
  const [store, setStore] = useState("all");
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [wizard, setWizard] = useState(false);

  useEffect(() => { api.get("/staff/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {}); }, []);
  const load = () => { setLoading(true); api.get(`/inventory/pos?account_id=${store}`).then(({ data }) => setPos(data.pos || [])).catch(() => setPos([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [store]);

  const openUpload = () => {
    if (store === "all") { toast.error("Pick a specific store first to upload a PO for it."); return; }
    setWizard(true);
  };

  return (
    <div className="space-y-4" data-testid="admin-inventory-pos">
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs uppercase tracking-wider font-medium text-slate-500">Store</label>
        <select value={store} onChange={(e) => setStore(e.target.value)} data-testid="inv-store-filter" className={inputCls + " bg-white"}>
          <option value="all">All stores</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.company_name}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => window.open(`${API_BASE}/inventory/pos/template`, "_blank", "noopener")} data-testid="admin-po-template" className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-4 py-2 rounded-md">
            <Download className="w-4 h-4" /> Download template
          </button>
          <button onClick={openUpload} data-testid="admin-upload-po" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-md">
            <Upload className="w-4 h-4" /> Upload PO
          </button>
        </div>
      </div>
      {loading ? <div className="flex items-center gap-2 text-slate-400 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        : pos.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center">No POs found. Pick a store and click <b>Upload PO</b> to add one.</p>
        : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50"><th className="py-3 px-4 font-medium"></th><th className="py-3 px-4 font-medium">Reference</th><th className="py-3 px-4 font-medium">Store</th><th className="py-3 px-4 font-medium">PO #</th><th className="py-3 px-4 font-medium">Lines</th><th className="py-3 px-4 font-medium">Units</th><th className="py-3 px-4 font-medium">Status</th><th className="py-3 px-4 font-medium">Created</th></tr></thead>
            <tbody>
              {pos.map((po) => (
                <React.Fragment key={po.id}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(expanded === po.id ? null : po.id)} data-testid={`inv-po-row-${po.id}`}>
                    <td className="py-3 px-4">{expanded === po.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}</td>
                    <td className="py-3 px-4 font-mono font-semibold text-slate-900">{po.boxhero_ref}</td>
                    <td className="py-3 px-4 text-slate-600">{po.company_name}</td>
                    <td className="py-3 px-4 text-slate-600">{po.customer_po_number || "—"}</td>
                    <td className="py-3 px-4 text-slate-600">{po.line_count}</td>
                    <td className="py-3 px-4 text-slate-600">{po.expected_units}</td>
                    <td className="py-3 px-4"><StatusPill s={po.status} /></td>
                    <td className="py-3 px-4 text-slate-500 whitespace-nowrap">{formatDate(po.created_at)}</td>
                  </tr>
                  {expanded === po.id && <tr><td colSpan={8} className="bg-slate-50/60 px-6 py-4"><PODetail poId={po.id} onStatus={load} /></td></tr>}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {wizard && store !== "all" && <POWizard accountId={store} onClose={() => setWizard(false)} onCreated={load} />}
    </div>
  );
}

function LocationsTab() {
  const [locs, setLocs] = useState([]);
  const [form, setForm] = useState({ code: "", zone: "", location_type: "bin", capacity: "" });
  const [loading, setLoading] = useState(true);
  const [pasteText, setPasteText] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const load = () => { setLoading(true); api.get("/inventory/locations").then(({ data }) => setLocs(data.locations || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.code.trim()) { toast.error("Location code required"); return; }
    try { await api.post("/inventory/locations", { ...form, capacity: form.capacity ? Number(form.capacity) : null }); setForm({ code: "", zone: "", location_type: "bin", capacity: "" }); load(); toast.success("Location added"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/inventory/locations/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.added} added, ${data.updated} updated`); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const importPaste = async () => {
    const codes = pasteText.split(/[\n,]+/).map((c) => c.trim()).filter(Boolean);
    if (!codes.length) { toast.error("Paste some location codes first"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/inventory/locations/bulk", { codes });
      toast.success(`Imported: ${data.added} added, ${data.updated} updated`); setPasteText(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };
  return (
    <div className="space-y-4" data-testid="admin-inventory-locations">
      {/* Bulk import */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); importFile(e.dataTransfer.files?.[0]); }} data-testid="loc-dropzone" className="cursor-pointer border-2 border-dashed border-slate-300 hover:border-blue-400 rounded-xl p-5 text-center transition-colors">
          {importing ? <Loader2 className="w-6 h-6 mx-auto text-blue-500 animate-spin" /> : <Upload className="w-6 h-6 mx-auto text-slate-400" />}
          <p className="text-sm font-semibold text-slate-700 mt-2">Drag & drop a CSV/XLSX of locations</p>
          <p className="text-xs text-slate-400">Columns: Code, Zone, Type, Capacity (existing codes are updated). Capacity optional — no cap enforced.</p>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={(e) => importFile(e.target.files?.[0])} className="hidden" data-testid="loc-import-file" />
        </div>
        <div className="border border-slate-200 rounded-xl p-4">
          <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Or paste codes (one per line)</label>
          <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={3} data-testid="loc-paste" placeholder={"G-C1\nG-C2\nH-B19"} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono" />
          <button onClick={importPaste} disabled={importing} data-testid="loc-paste-import" className="mt-2 inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold px-4 py-2 rounded-md">{importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Import list</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-2">
        <div><label className="block text-[10px] uppercase text-slate-400 mb-1">Code</label><input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} data-testid="loc-code" placeholder="G-C4" className={inputCls + " w-28 uppercase"} /></div>
        <div><label className="block text-[10px] uppercase text-slate-400 mb-1">Zone</label><input value={form.zone} onChange={(e) => setForm((f) => ({ ...f, zone: e.target.value }))} data-testid="loc-zone" placeholder="G" className={inputCls + " w-20"} /></div>
        <div><label className="block text-[10px] uppercase text-slate-400 mb-1">Type</label>
          <select value={form.location_type} onChange={(e) => setForm((f) => ({ ...f, location_type: e.target.value }))} data-testid="loc-type" className={inputCls + " bg-white"}>
            {["bin", "shelf", "floor", "pallet", "staging", "damaged", "quarantine"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div><label className="block text-[10px] uppercase text-slate-400 mb-1">Capacity</label><input type="number" value={form.capacity} onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))} data-testid="loc-capacity" className={inputCls + " w-24"} /></div>
        <button onClick={create} data-testid="loc-add-btn" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-md"><Plus className="w-4 h-4" /> Add</button>
      </div>
      {loading ? <div className="flex items-center gap-2 text-slate-400 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
        : <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="locations-table">
          <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50"><th className="py-2.5 px-4 font-medium">Code</th><th className="py-2.5 px-4 font-medium">Zone</th><th className="py-2.5 px-4 font-medium">Type</th><th className="py-2.5 px-4 font-medium">Capacity</th></tr></thead>
          <tbody>{locs.map((l) => <tr key={l.id} className="border-b border-slate-100"><td className="py-2.5 px-4 font-mono font-semibold">{l.code}</td><td className="py-2.5 px-4 text-slate-600">{l.zone || "—"}</td><td className="py-2.5 px-4 text-slate-600 capitalize">{l.location_type}</td><td className="py-2.5 px-4 text-slate-600">{l.capacity ?? "—"}</td></tr>)}
            {locs.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-slate-400">No locations yet.</td></tr>}</tbody>
        </table>
      </div>}
    </div>
  );
}

function DefaultsTab() {
  const [accounts, setAccounts] = useState([]);
  const [store, setStore] = useState("");
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/staff/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {}); }, []);
  const pick = (id) => { setStore(id); setD(null); if (id) api.get(`/admin/accounts/${id}/product-defaults`).then(({ data }) => setD(data.product_defaults)).catch(() => {}); };
  const save = async () => { setSaving(true); try { await api.put(`/admin/accounts/${store}/product-defaults`, d); toast.success("Defaults saved"); } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); } };
  const upd = (k, type) => (e) => setD((x) => ({ ...x, [k]: type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value }));
  return (
    <div className="space-y-4" data-testid="admin-inventory-defaults">
      <select value={store} onChange={(e) => pick(e.target.value)} data-testid="pd-store" className={inputCls + " bg-white w-72"}>
        <option value="">Select a store…</option>
        {accounts.map((a) => <option key={a.id} value={a.id}>{a.company_name}</option>)}
      </select>
      {store && !d && <div className="flex items-center gap-2 text-slate-400 py-8"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>}
      {d && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <p className="text-sm text-slate-500">These defaults fill in weight, dimensions and customs when new products are created and exported to ShipStation.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["weight_oz", "Weight (oz)", "number"], ["length", "Length", "number"], ["width", "Width", "number"], ["height", "Height", "number"], ["customs_description", "Customs desc", "text"], ["customs_value", "Customs value", "number"], ["tariff_no", "Tariff #", "text"], ["country_of_origin", "Country", "text"]].map(([k, label, type]) => (
              <div key={k}>
                <label className="block text-[10px] uppercase text-slate-400 mb-1">{label}</label>
                <input type={type} value={d[k] ?? ""} onChange={upd(k, type)} data-testid={`pd-${k}`} className={inputCls + " w-full"} />
              </div>
            ))}
          </div>
          <div className="flex justify-end"><button onClick={save} disabled={saving} data-testid="pd-save" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2 rounded-md">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save defaults</button></div>
        </div>
      )}
    </div>
  );
}

const CHART_MARGIN = { top: 5, right: 10, left: -20, bottom: 0 };

function InvKPI({ label, value, sub, testid, tone = "slate" }) {
  return (
    <div className="rounded-xl border p-4 bg-white border-slate-200" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-2xl font-extrabold text-${tone}-700 mt-0.5`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function AnalyticsTab() {
  const [accounts, setAccounts] = useState([]);
  const [store, setStore] = useState("all");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/staff/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    api.get(`/inventory/analytics/receiving?days=${days}&account_id=${store}`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [store, days]);

  const t = data?.totals || {};
  const openByStatus = Object.entries(data?.open_pos?.by_status || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ status: PO_STATUS_META[k]?.label || k, count: v }));

  return (
    <div className="space-y-4" data-testid="admin-inventory-analytics">
      <div className="flex flex-wrap items-center gap-2">
        <select value={store} onChange={(e) => setStore(e.target.value)} data-testid="an-store" className={inputCls + " bg-white"}>
          <option value="all">All stores</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.company_name}</option>)}
        </select>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`an-days-${d}`} className={`px-3 py-2 text-sm font-semibold rounded-md ${days === d ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{d}d</button>
          ))}
        </div>
      </div>

      {loading ? <div className="flex items-center gap-2 text-slate-400 py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading analytics…</div>
        : !data ? <p className="text-sm text-slate-400 py-10 text-center">No analytics available.</p>
        : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <InvKPI label="Units received" value={t.units || 0} sub={`${t.scans || 0} scans`} testid="an-kpi-units" tone="blue" />
            <InvKPI label="Good (sellable)" value={t.good || 0} testid="an-kpi-good" tone="emerald" />
            <InvKPI label="Held" value={t.held || 0} testid="an-kpi-held" tone="red" />
            <InvKPI label="Open POs" value={data.open_pos?.total || 0} testid="an-kpi-open" tone="orange" />
            <InvKPI label="Discrepancy rate" value={`${data.discrepancy_rate || 0}%`} sub={`${data.discrepancy_pos}/${data.completed_pos} POs`} testid="an-kpi-disc" tone="amber" />
            <InvKPI label="Avg hands-on" value={formatDuration(data.avg_duration_seconds)} testid="an-kpi-dur" tone="slate" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-600" /> Throughput (units/day)</p>
              {data.throughput?.length ? (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={data.throughput} margin={CHART_MARGIN}>
                    <defs><linearGradient id="thg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563eb" stopOpacity={0.5} /><stop offset="100%" stopColor="#2563eb" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => (d || "").slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="units" stroke="#2563eb" fill="url(#thg)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400 py-10 text-center">No receiving activity in this window.</p>}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-orange-500" /> Open POs by stage</p>
              {openByStatus.length ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={openByStatus} margin={CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="status" tick={{ fontSize: 9 }} interval={0} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#f97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400 py-10 text-center">No open POs.</p>}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <p className="text-sm font-bold text-slate-800 px-4 pt-4 pb-2 flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" /> Staff contributions</p>
            <table className="w-full text-left text-sm" data-testid="an-staff-table">
              <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-y border-slate-200 bg-slate-50"><th className="py-2.5 px-4 font-medium">Staff</th><th className="py-2.5 px-4 font-medium">Units</th><th className="py-2.5 px-4 font-medium">Good</th><th className="py-2.5 px-4 font-medium">Held</th><th className="py-2.5 px-4 font-medium">Scans</th></tr></thead>
              <tbody>
                {(data.staff || []).map((s) => (
                  <tr key={s.user} className="border-b border-slate-100" data-testid={`an-staff-${s.user}`}>
                    <td className="py-2.5 px-4 font-medium text-slate-700">{s.user}</td>
                    <td className="py-2.5 px-4 font-bold">{s.units}</td>
                    <td className="py-2.5 px-4 text-emerald-700">{s.good}</td>
                    <td className="py-2.5 px-4 text-red-600">{s.held}</td>
                    <td className="py-2.5 px-4 text-slate-500">{s.scans}</td>
                  </tr>
                ))}
                {(data.staff || []).length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No scan activity in this window.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ExportTab() {
  const [accounts, setAccounts] = useState([]);
  const [store, setStore] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/staff/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {}); }, []);
  const pick = (id) => {
    setStore(id); setPreview(null);
    if (id) { setLoading(true); api.get(`/inventory/export/shipstation/preview?account_id=${id}`).then(({ data }) => setPreview(data)).catch(() => setPreview(null)).finally(() => setLoading(false)); }
  };
  const download = (format) => { if (!store) return; window.open(`${API_BASE}/inventory/export/shipstation?account_id=${store}&format=${format}`, "_blank", "noopener"); };

  return (
    <div className="space-y-4" data-testid="admin-inventory-export">
      <p className="text-sm text-slate-500">Exports the <b>AUTO_ShipStation</b> product upload sheet for every SKU on this store&apos;s POs. <b>WarehouseLocation</b> fills in as items are received and lists only the bin codes (<span className="font-mono">G-C1; H-B19</span>) — the per-bin quantity shows in the preview below but is left out of the file. Not-yet-received SKUs show <span className="font-mono">--</span>.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select value={store} onChange={(e) => pick(e.target.value)} data-testid="exp-store" className={inputCls + " bg-white w-72"}>
          <option value="">Select a store…</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.company_name}</option>)}
        </select>
        {store && preview && (
          <div className="flex gap-2">
            <button onClick={() => download("csv")} data-testid="exp-download-csv" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-md"><Download className="w-4 h-4" /> CSV</button>
            <button onClick={() => download("xlsx")} data-testid="exp-download-xlsx" className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-md"><FileSpreadsheet className="w-4 h-4" /> XLSX</button>
          </div>
        )}
      </div>
      {loading ? <div className="flex items-center gap-2 text-slate-400 py-10"><Loader2 className="w-5 h-5 animate-spin" /> Building preview…</div>
        : preview && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs text-slate-600 font-semibold">{preview.count} SKUs · {preview.located_count ?? 0} located · {preview.total_units} sellable units</div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="exp-preview-table">
              <thead><tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200"><th className="py-2 px-4 font-medium">SKU</th><th className="py-2 px-4 font-medium">Name</th><th className="py-2 px-4 font-medium">UPC</th><th className="py-2 px-4 font-medium">Qty</th><th className="py-2 px-4 font-medium">Warehouse location</th></tr></thead>
              <tbody>
                {preview.products.map((p) => (
                  <tr key={p.sku} className="border-b border-slate-50">
                    <td className="py-2 px-4 font-mono text-xs font-semibold">{p.sku}</td>
                    <td className="py-2 px-4 text-slate-700">{p.name}</td>
                    <td className="py-2 px-4 font-mono text-xs text-slate-500">{p.barcode || "—"}</td>
                    <td className="py-2 px-4 font-bold">{p.quantity}</td>
                    <td className="py-2 px-4 font-mono text-xs text-slate-600">{p.warehouse_location}</td>
                  </tr>
                ))}
                {preview.products.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No sellable stock to export yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ImportStockTab() {
  const [accounts, setAccounts] = useState([]);
  const [store, setStore] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => { api.get("/staff/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {}); }, []);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/inventory/stock/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "inventory_import_template.csv"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download template"); }
  };

  const importFile = async (file) => {
    if (!file) return;
    if (!store) { toast.error("Pick a store first"); return; }
    setImporting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("account_id", store);
      const { data } = await api.post("/inventory/stock/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated${data.error_count ? `, ${data.error_count} errors` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-4" data-testid="admin-inventory-import">
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-900">
        Import all the on-hand stock you are holding today. Download the template, fill in <span className="font-mono font-semibold">sku, barcode, product_name, location_code, quantity</span>, and upload. Barcodes let staff scan-verify returns.
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={store} onChange={(e) => { setStore(e.target.value); setResult(null); }} data-testid="import-store" className={inputCls + " bg-white w-72"}>
          <option value="">Select a store…</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.company_name}</option>)}
        </select>
        <button onClick={downloadTemplate} data-testid="import-template-btn" className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-4 py-2 rounded-md">
          <Download className="w-4 h-4" /> Download template
        </button>
      </div>
      <div onClick={() => store && fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); importFile(e.dataTransfer.files?.[0]); }}
        data-testid="import-dropzone" className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${store ? "cursor-pointer border-slate-300 hover:border-blue-400" : "border-slate-200 opacity-50"}`}>
        {importing ? <Loader2 className="w-7 h-7 mx-auto text-blue-500 animate-spin" /> : <Upload className="w-7 h-7 mx-auto text-slate-400" />}
        <p className="text-sm text-slate-600 mt-2 font-medium">{store ? "Drop your CSV here or click to browse" : "Pick a store to enable import"}</p>
        <input ref={fileRef} type="file" accept=".csv" onChange={(e) => importFile(e.target.files?.[0])} className="hidden" data-testid="import-file" />
      </div>
      {result && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="import-result">
          <div className="flex items-center gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold"><CheckCircle2 className="w-4 h-4" /> {result.created} added</span>
            <span className="inline-flex items-center gap-1.5 text-blue-700 font-semibold">{result.updated} updated</span>
            {result.error_count > 0 && <span className="inline-flex items-center gap-1.5 text-red-600 font-semibold"><AlertTriangle className="w-4 h-4" /> {result.error_count} errors</span>}
          </div>
          {(result.errors || []).length > 0 && (
            <ul className="mt-3 text-xs text-red-600 space-y-0.5 max-h-40 overflow-y-auto">
              {result.errors.map((er, i) => <li key={`err-${i}`}>{er}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminInventory({ isReturnsManager = false }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState("pos");
  const subtabs = SUBTABS.filter((t) => !isReturnsManager || t.v !== "defaults");
  return (
    <div className="space-y-5" data-testid="admin-inventory">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Inventory Receiving</h2>
          <p className="text-slate-500 text-sm mt-1">Review inbound POs, plan warehouse locations{isReturnsManager ? "" : " and manage product defaults"}, and receive against a PO with the scan station.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => navigate("/training?topic=receiving")} data-testid="open-training" className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-bold px-4 py-2.5 rounded-md"><BookOpen className="w-4 h-4" /> How-to guide</button>
          <button onClick={() => navigate("/move-stock")} data-testid="open-move-stock" className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-bold px-4 py-2.5 rounded-md"><Shuffle className="w-4 h-4" /> Move stock</button>
          <button onClick={() => navigate("/receiving-inventory")} data-testid="open-receiving-station" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2.5 rounded-md"><ScanLine className="w-4 h-4" /> Open scan station</button>
        </div>
      </div>
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {subtabs.map((t) => (
          <button key={t.v} onClick={() => setSub(t.v)} data-testid={`inv-subtab-${t.v}`} className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${sub === t.v ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}><t.icon className="w-4 h-4" /> {t.l}</button>
        ))}
      </div>
      {sub === "pos" && <POsTab />}
      {sub === "analytics" && <AnalyticsTab />}
      {sub === "locations" && <LocationsTab />}
      {sub === "import" && <ImportStockTab />}
      {sub === "export" && <ExportTab />}
      {sub === "defaults" && !isReturnsManager && <DefaultsTab />}
    </div>
  );
}
